// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Settings: Live sending, test mode, details, delivery (retention, rate limit),
// status, workload identities, and the danger zone.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { applicationQuery } from '@/api/queries/applications'
import { usePermissions } from '@/auth/usePermissions'
import { ErrorState } from '@/components/QueryState'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { SkeletonText } from '@/components/ui/skeleton'
import {
  DangerZone,
  DeliverySettings,
  GeneralSettings,
  StatusSettings,
  TestModeSettings,
} from '@/features/ops/ApplicationSettingsForms'
import { LiveSendingSettings } from '@/features/ops/LiveSending'
import { UnsavedChangesScope } from '@/features/ops/UnsavedChangesScope'
import { WorkloadIdentities } from '@/features/ops/WorkloadIdentities'

export const Route = createFileRoute('/applications/$appId/settings')({
  staticData: { crumb: 'Settings' },
  component: AppSettingsPage,
})

function AppSettingsPage() {
  const { appId } = Route.useParams()
  const app = useQuery(applicationQuery(appId))
  const { can } = usePermissions()

  if (app.isError) return <ErrorState error={app.error} onRetry={() => void app.refetch()} />
  if (!app.data)
    return (
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <Card key={i}>
            <CardBody className="pt-4">
              <SkeletonText lines={6} />
            </CardBody>
          </Card>
        ))}
      </div>
    )

  const a = app.data
  // One unsaved-changes prompt for every form on the page.
  return (
    <UnsavedChangesScope>
      <div className="space-y-5">
        {!can('manage_config') && (
          <p className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
            You can view these settings. Only Admins can change them.
          </p>
        )}
        <LiveSendingSettings app={a} />
        <TestModeSettings app={a} />
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 xl:grid-cols-2">
          <GeneralSettings app={a} />
          <DeliverySettings app={a} />
        </div>
        <Card>
          <StatusSettings app={a} />
        </Card>
        <Card id="workload-identities" className="scroll-mt-6">
          <CardHeader
            title="Workload identities"
            description="Entra applications (managed identities or app registrations) allowed to call the send API as this application."
          />
          <CardBody>
            <WorkloadIdentities appId={appId} />
          </CardBody>
        </Card>
        {can('manage_config') && <DangerZone app={a} />}
      </div>
    </UnsavedChangesScope>
  )
}
